import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  Divider,
  Fade,
  Grow,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import {
  Collections as ContentIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, contentAPI } from '../services/api';
import { toast } from 'react-toastify';

const StatCard = ({ title, value, icon, color = 'primary', delay = 0, size = 'normal', isPrimary = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const mainColor = theme.palette[color]?.main || theme.palette.primary.main;
  const isLarge = size === 'large' || isPrimary;

  return (
    <Grow in={true} timeout={500} style={{ transformOrigin: '0 0 0' }} {...{ timeout: 500 + delay }}>
      <Card
        sx={{
          height: '100%',
          position: 'relative',
          overflow: 'visible',
          borderTop: `4px solid ${mainColor} !important`,
          backgroundColor: isPrimary ? 'rgba(34, 197, 94, 0.02)' : (isLarge ? 'rgba(247, 148, 29, 0.02)' : '#ffffff'),
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: isPrimary ? '0 4px 12px rgba(34, 197, 94, 0.12)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: isPrimary ? '0 12px 32px rgba(34, 197, 94, 0.15)' : (isLarge ? '0 12px 32px rgba(247, 148, 29, 0.15)' : '0 8px 20px rgba(0, 0, 0, 0.1)'),
          },
        }}
      >
        <CardContent
          sx={{
            pb: `${isPrimary ? '28px' : (isLarge ? '32px' : '24px')} !important`,
            pt: isPrimary ? '24px' : (isLarge ? '28px' : '20px'),
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: isPrimary ? '0.75rem' : (isLarge ? '0.8rem' : '0.75rem'),
                  mb: 1.5,
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{
                  fontSize: isPrimary ? '2.25rem' : (isLarge ? '2.5rem' : '1.75rem'),
                  fontWeight: 800,
                  color: isPrimary ? '#22c55e' : (isLarge ? mainColor : '#1e293b'),
                  lineHeight: 1,
                }}
              >
                {value}
              </Typography>
            </Box>
            <Box
              sx={{
                background: isPrimary ? '#22c55e15' : `${mainColor}15`,
                color: isPrimary ? '#22c55e' : mainColor,
                borderRadius: '12px',
                width: isPrimary ? 64 : (isLarge ? 64 : 56),
                height: isPrimary ? 64 : (isLarge ? 64 : 56),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: isPrimary ? '#22c55e25' : `${mainColor}25`,
                  transform: 'scale(1.1)',
                },
              }}
            >
              {React.cloneElement(icon, {
                sx: { fontSize: isPrimary ? 36 : (isLarge ? 36 : 28), ...icon.props.sx },
              })}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grow>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [stats, setStats] = useState({
    totalContent: 0,
    pendingContent: 0,
    approvedContent: 0,
    rejectedContent: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);

      const statsResponse = await dashboardAPI.getStats();
      const statsData = statsResponse.data?.data?.stats || {};

      setStats({
        totalContent: statsData.totalContent || 0,
        pendingContent: statsData.pendingContent || 0,
        approvedContent: statsData.approvedContent || 0,
        rejectedContent: statsData.rejectedContent || 0,
      });

      const recentResponse = await contentAPI.getMyContent({ limit: 6 });
      const recentContent = recentResponse.data?.data?.content || [];

      const activityFromContent = recentContent
        .slice(0, 6)
        .map((item) => ({
          _id: item._id,
          action: `Content "${item.filename}"`,
          details: `Status: ${item.status}`,
          timestamp: item.uploadedAt,
          status: item.status,
        }));

      setRecentActivity(activityFromContent);

      if (showRefresh) {
        toast.success('Dashboard refreshed');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  const getStatusChipColor = (status) => {
    if (!status) return 'default';
    if (status.includes('pending')) return 'warning';
    if (status.includes('approved') || status.includes('active')) return 'success';
    if (status.includes('rejected')) return 'error';
    return 'default';
  };

  return (
    <Box>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#1e293b',
              mb: 0.5,
              fontSize: '1.5rem',
            }}
          >
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.username}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              fontSize: '0.9rem',
            }}
          >
            Track and manage your uploaded content
          </Typography>
        </Box>
        <IconButton
          onClick={handleRefresh}
          disabled={refreshing}
          sx={{
            backgroundColor: '#F7941D',
            color: '#ffffff',
            borderRadius: '8px',
            p: 1.25,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#C57617',
              transform: 'rotate(180deg)',
            },
            '&:disabled': {
              backgroundColor: '#cbd5e1',
            },
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Statistics Cards Grid */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Content"
            value={stats.totalContent}
            icon={<ContentIcon />}
            color="primary"
            delay={0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Approval"
            value={stats.pendingContent}
            icon={<PendingIcon />}
            color="warning"
            delay={100}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active / Approved"
            value={stats.approvedContent}
            icon={<ApprovedIcon />}
            color="success"
            delay={200}
            isPrimary={true}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Rejected"
            value={stats.rejectedContent}
            icon={<RejectedIcon />}
            color="error"
            delay={300}
          />
        </Grid>
      </Grid>

      {/* Recent Activity Section */}
      <Fade in={true} timeout={800}>
        <Paper
          sx={{
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8f9fb' }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#1e293b',
                fontSize: '1.1rem',
              }}
            >
              Recent Activity
            </Typography>
          </Box>

          <Box sx={{ p: isMobile ? 2 : 3 }}>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  Loading activity...
                </Typography>
              </Box>
            ) : recentActivity.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                  px: 2,
                  backgroundColor: '#f8f9fb',
                  borderRadius: '8px',
                  border: '1px dashed #cbd5e1',
                }}
              >
                <TrendingIcon
                  sx={{
                    fontSize: '2.5rem',
                    color: '#cbd5e1',
                    mb: 1.5,
                  }}
                />
                <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  No content yet
                </Typography>
                <Typography color="text.secondary" variant="caption" sx={{ display: 'block', mb: 2 }}>
                  Upload your first image or video to get started
                </Typography>
                {stats.totalContent === 0 && !loading && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate('/upload')}
                    sx={{
                      backgroundColor: '#F7941D',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#C57617',
                      },
                    }}
                  >
                    Upload Content
                  </Button>
                )}
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {recentActivity.map((activity, index) => (
                  <React.Fragment key={activity._id}>
                    <Fade in={true} timeout={300} style={{ transitionDelay: `${index * 40}ms` }}>
                      <ListItem
                        alignItems="center"
                        sx={{
                          px: 0,
                          py: 1.25,
                          borderRadius: '8px',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#f8f9fb',
                            pl: 0.5,
                          },
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: '#1e293b',
                              mb: 0.25,
                              fontSize: '0.9rem',
                            }}
                          >
                            {activity.action}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1,
                              alignItems: 'center',
                              flexWrap: 'wrap',
                            }}
                          >
                            <Chip
                              label={activity.status?.replace('_', ' ')}
                              size="small"
                              color={getStatusChipColor(activity.status)}
                              variant="filled"
                              sx={{
                                height: '20px',
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                opacity: 0.85,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#94a3b8',
                                fontSize: '0.75rem',
                              }}
                            >
                              {new Date(activity.timestamp).toLocaleDateString()} at{' '}
                              {new Date(activity.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Typography>
                          </Box>
                        </Box>
                      </ListItem>
                    </Fade>
                    {index < recentActivity.length - 1 && (
                      <Divider component="li" sx={{ my: 0, borderColor: '#e2e8f0' }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Paper>
      </Fade>


    </Box>
  );
};

export default Dashboard;
